import { noop, toServerDuration, SKIPPED, addDuration } from '@datadog/browser-core';
import { discardNegativeDuration } from '../discardNegativeDuration';
import { RumEventType } from '../../rawRumEvent.types';
import { trackClickActions } from './trackClickActions';
import { trackManualActions } from './trackManualActions';
export const LONG_TASK_START_TIME_CORRECTION = 1;
export function startActionCollection(lifeCycle, hooks, domMutationObservable, windowOpenObservable, configuration) {
    const { unsubscribe: unsubscribeAutoAction } = lifeCycle.subscribe(0 /* LifeCycleEventType.AUTO_ACTION_COMPLETED */, (action) => {
        lifeCycle.notify(12 /* LifeCycleEventType.RAW_RUM_EVENT_COLLECTED */, processAction(action));
    });
    const stopClickActions = noop;
    let clickActions;
    if (configuration.trackUserInteractions) {
        clickActions = trackClickActions(lifeCycle, domMutationObservable, windowOpenObservable, configuration);
    }
    const manualActions = trackManualActions(lifeCycle, (action) => {
        lifeCycle.notify(12 /* LifeCycleEventType.RAW_RUM_EVENT_COLLECTED */, processAction(action));
    });
    const actionContexts = {
        findActionId: (startTime) => {
            var _a;
            const manualActionId = manualActions.findActionId(startTime);
            const clickActionId = (_a = clickActions === null || clickActions === void 0 ? void 0 : clickActions.findActionId(startTime)) !== null && _a !== void 0 ? _a : [];
            return manualActionId.concat(clickActionId);
        },
    };
    hooks.register(0 /* HookNames.Assemble */, ({ startTime, eventType }) => {
        if (eventType !== RumEventType.ERROR &&
            eventType !== RumEventType.RESOURCE &&
            eventType !== RumEventType.LONG_TASK) {
            return SKIPPED;
        }
        // Long tasks triggered by interaction handlers (pointerup, click, etc.)
        // can have a start time slightly before the interaction timestamp (long_task.start_time < action.start_time).
        // This likely happens because the interaction timestamp is recorded during the event dispatch,
        // not at the beginning of the rendering frame. I observed a difference of < 1 ms in my tests.
        // Fixes flakiness in test: "associates long tasks to interaction actions"
        const correctedStartTime = eventType === RumEventType.LONG_TASK ? addDuration(startTime, LONG_TASK_START_TIME_CORRECTION) : startTime;
        const actionId = actionContexts.findActionId(correctedStartTime);
        if (!actionId.length) {
            return SKIPPED;
        }
        return {
            type: eventType,
            action: { id: actionId },
        };
    });
    hooks.register(1 /* HookNames.AssembleTelemetry */, ({ startTime }) => ({
        // todo: fix telemetry event type
        action: { id: actionContexts.findActionId(startTime) },
    }));
    return {
        addAction: manualActions.addAction,
        startAction: manualActions.startAction,
        stopAction: manualActions.stopAction,
        actionContexts,
        stop: () => {
            unsubscribeAutoAction();
            stopClickActions();
            manualActions.stop();
            clickActions === null || clickActions === void 0 ? void 0 : clickActions.stop();
        },
    };
}
function processAction(action) {
    var _a, _b, _c, _d;
    const isAuto = isAutoAction(action);
    const loadingTime = discardNegativeDuration(toServerDuration(action.duration));
    return {
        rawRumEvent: {
            type: RumEventType.ACTION,
            date: action.startClocks.timeStamp,
            action: {
                id: action.id,
                target: { name: action.name },
                type: action.type,
                ...(loadingTime !== undefined && { loading_time: loadingTime }),
                ...(action.counts && {
                    error: { count: action.counts.errorCount },
                    long_task: { count: action.counts.longTaskCount },
                    resource: { count: action.counts.resourceCount },
                }),
                frustration: { type: action.frustrationTypes },
            },
            ...(isAuto
                ? {
                    _dd: {
                        action: {
                            target: {
                                selector: ((_a = action.target) === null || _a === void 0 ? void 0 : _a.selector) || undefined,
                                width: ((_b = action.target) === null || _b === void 0 ? void 0 : _b.width) || undefined,
                                height: ((_c = action.target) === null || _c === void 0 ? void 0 : _c.height) || undefined,
                                composed_path_selector: (_d = action.target) === null || _d === void 0 ? void 0 : _d.composedPathSelector,
                            },
                            position: action.position,
                            name_source: action.nameSource,
                        },
                    },
                }
                : { context: action.context }),
        },
        duration: action.duration,
        startClocks: action.startClocks,
        domainContext: isAuto ? { events: action.events } : { handlingStack: action.handlingStack },
    };
}
function isAutoAction(action) {
    return 'events' in action;
}
//# sourceMappingURL=actionCollection.js.map